import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="relative flex min-h-screen items-center justify-center bg-cover bg-center"
      style={{ backgroundImage: "url(https://images.pexels.com/photos/8640331/pexels-photo-8640331.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)" }}
    >
      <div className="absolute inset-0 bg-black/85" />
      <div className="relative z-10 w-full max-w-md border border-border bg-[#0A0A0A] p-10">
        <div className="mb-8 flex items-center gap-3">
          <Activity size={24} className="text-primary drop-shadow-[0_0_8px_rgba(0,229,255,0.6)]" />
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-[0.3em] text-white">NEXUS</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-500">Accès administrateur</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-border bg-[#050505] px-3 py-2.5 font-mono text-sm text-white outline-none transition-colors duration-150 focus:border-primary"
              placeholder="votre@email.com"
            />
          </div>
          <div>
            <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">Mot de passe</label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full border border-border bg-[#050505] px-3 py-2.5 pr-11 font-mono text-sm text-white outline-none transition-colors duration-150 focus:border-primary"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                title={showPassword ? "Masquer le mot de passe" : "Voir le mot de passe"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 transition-colors duration-150 hover:text-primary"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          {error && (
            <div data-testid="login-error" className="border border-red-500/40 bg-red-500/10 px-3 py-2 font-mono text-xs text-red-400">
              {error}
            </div>
          )}
          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full bg-primary py-3 font-mono text-xs font-bold uppercase tracking-[0.3em] text-black transition-opacity duration-150 hover:opacity-90 disabled:opacity-50"
          >
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </form>
        <p className="mt-6 font-mono text-[9px] uppercase tracking-[0.15em] text-neutral-600">
          BrainNexora — Plateforme universelle de connexion · Cerveau Central
        </p>
      </div>
    </div>
  );
}
